import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT, PLAYER_SPEED, ZOMBIE_SPEED_SHAMBLER, ROCKET_SPEED, BLAST_RADIUS, ROCKET_LIFETIME, HOMING_ROCKETS_MAX, ROCKET_PICKUP_CHANCE } from '../constants.js';

/**
 * BaseGameScene - Abstract base class for all gameplay levels
 *
 * Contains shared logic for:
 * - Player movement and controls
 * - Weapon systems (shells, homing rockets)
 * - Zombie spawning and AI
 * - Airstrike helicopter system
 * - Cheat code system
 * - HUD utilities
 *
 * Subclasses must implement:
 * - getLevelConfig() - returns level dimensions, zombie counts, etc.
 * - createBackground() - level-specific map
 * - createBuildings() - level-specific structures (optional)
 * - createHUD() - level-specific HUD layout
 * - updateHUD() - level-specific HUD updates
 * - levelComplete() - level-specific victory sequence
 * - gameOver() - level-specific death sequence
 * - getNextLevelScene() - scene key for /next! cheat
 */
export default class BaseGameScene extends Phaser.Scene {
  constructor(config) {
    super(config);
  }

  // ==================== ABSTRACT METHODS (must be implemented by subclass) ====================

  /**
   * Returns level configuration
   * @returns {{ width: number, height: number, initialZombies: number, zombieSpawnDistance: number }}
   */
  getLevelConfig() {
    throw new Error('getLevelConfig() must be implemented by subclass');
  }

  /**
   * Creates level-specific background/map
   */
  createBackground() {
    throw new Error('createBackground() must be implemented by subclass');
  }

  /**
   * Creates level-specific HUD
   */
  createHUD() {
    throw new Error('createHUD() must be implemented by subclass');
  }

  /**
   * Updates level-specific HUD
   */
  updateHUD() {
    throw new Error('updateHUD() must be implemented by subclass');
  }

  /**
   * Level-specific victory sequence
   */
  levelComplete() {
    throw new Error('levelComplete() must be implemented by subclass');
  }

  /**
   * Level-specific death sequence
   */
  gameOver() {
    throw new Error('gameOver() must be implemented by subclass');
  }

  /**
   * Returns the scene key to transition to for /next! cheat
   */
  getNextLevelScene() {
    throw new Error('getNextLevelScene() must be implemented by subclass');
  }

  // ==================== OPTIONAL OVERRIDES ====================

  /**
   * Creates level-specific buildings (optional)
   */
  createBuildings() {
    // Default: no buildings - subclass can override
  }

  /**
   * Creates level-specific animations (optional)
   * Base implementation creates zombie animations
   */
  createAnimations() {
    // Check if animations already exist
    if (!this.anims.exists('zombie_walk')) {
      this.anims.create({
        key: 'zombie_walk',
        frames: this.anims.generateFrameNumbers('zombie_shambler_walk', { start: 0, end: 5 }),
        frameRate: 8,
        repeat: -1,
      });
    }

    if (!this.anims.exists('zombie_death')) {
      this.anims.create({
        key: 'zombie_death',
        frames: this.anims.generateFrameNumbers('zombie_shambler_death', { start: 0, end: 5 }),
        frameRate: 12,
        repeat: 0,
      });
    }
  }

  // ==================== CORE LIFECYCLE ====================

  init() {
    const config = this.getLevelConfig();
    this.levelWidth = config.width;
    this.levelHeight = config.height;
    this.initialZombies = config.initialZombies;
    this.zombieSpawnDistance = config.zombieSpawnDistance;

    this.player = null;
    this.zombies = null;
    this.bullets = null;
    this.cursors = null;
    this.score = 0;
    this.music = null;

    // Airstrike system
    this.airstrikeAvailable = true;
    this.activeHelicopter = null;
    this.helicopterBullets = null;
    this.rotorTimer = null;

    // Homing rocket weapon system
    this.weaponMode = 'shell'; // 'shell' or 'homing'
    this.homingRocketsRemaining = HOMING_ROCKETS_MAX;
    this.homingRockets = null;
    this.rocketPickups = null;

    // Event handler references for cleanup
    this.visibilityHandler = null;

    // Cheat code system
    this.cheatInputActive = false;
    this.cheatInputText = null;
    this.cheatBuffer = '';
    this.thorHelicopterActive = false;

    // Shooting cooldown
    this.canShoot = true;
    this.lastFired = 0;
  }

  create() {
    const config = this.getLevelConfig();

    // Set up world bounds
    this.physics.world.setBounds(0, 0, config.width, config.height);

    // Create animations
    this.createAnimations();

    // Create player FIRST (before anything that might reference it)
    this.createPlayer();

    // Create background/map
    this.createBackground();

    // Create level-specific buildings (if any)
    this.createBuildings();

    // Create zombie group
    this.zombies = this.physics.add.group();
    this.spawnZombies(config.initialZombies);

    // Create bullets group
    this.bullets = this.physics.add.group({
      defaultKey: 'bullet',
      maxSize: 20,
    });

    // Create homing rockets group
    this.homingRockets = this.physics.add.group({
      defaultKey: 'homing_rocket',
    });

    // Create rocket pickups group
    this.rocketPickups = this.physics.add.group();

    // Set up collisions
    this.physics.add.overlap(this.player, this.zombies, this.playerHitZombie, null, this);
    this.physics.add.overlap(this.bullets, this.zombies, this.bulletHitZombie, null, this);
    this.physics.add.overlap(this.homingRockets, this.zombies, this.rocketHitZombie, null, this);
    this.physics.add.overlap(this.player, this.rocketPickups, this.collectRocketPickup, null, this);

    // Set up input
    this.cursors = this.input.keyboard.createCursorKeys();
    this.wasd = this.input.keyboard.addKeys({
      up: Phaser.Input.Keyboard.KeyCodes.W,
      down: Phaser.Input.Keyboard.KeyCodes.S,
      left: Phaser.Input.Keyboard.KeyCodes.A,
      right: Phaser.Input.Keyboard.KeyCodes.D,
      shoot: Phaser.Input.Keyboard.KeyCodes.SPACE,
    });

    // Airstrike key (0)
    this.airstrikeKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ZERO);
    this.airstrikeKey.on('down', () => this.callAirstrike());

    // Weapon switch key (2)
    this.weaponSwitchKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.TWO);
    this.weaponSwitchKey.on('down', () => this.switchWeaponMode());

    // Create helicopter bullets group
    this.helicopterBullets = this.physics.add.group();

    // Ensure the canvas can receive focus
    const canvas = this.game?.canvas;
    if (canvas) {
      canvas.setAttribute('tabindex', '0');
      canvas.style.outline = 'none';
      canvas.focus();
    }
    this.input.on('pointerdown', () => {
      this.game?.canvas?.focus?.();
    });

    // Capture keys
    this.input.keyboard.addCapture([
      Phaser.Input.Keyboard.KeyCodes.UP,
      Phaser.Input.Keyboard.KeyCodes.DOWN,
      Phaser.Input.Keyboard.KeyCodes.LEFT,
      Phaser.Input.Keyboard.KeyCodes.RIGHT,
      Phaser.Input.Keyboard.KeyCodes.W,
      Phaser.Input.Keyboard.KeyCodes.A,
      Phaser.Input.Keyboard.KeyCodes.S,
      Phaser.Input.Keyboard.KeyCodes.D,
      Phaser.Input.Keyboard.KeyCodes.SPACE,
      Phaser.Input.Keyboard.KeyCodes.ZERO,
      Phaser.Input.Keyboard.KeyCodes.TWO,
    ]);

    // Reset input state on blur/focus
    const resetInputState = () => {
      if (this.input?.keyboard) {
        this.input.keyboard.resetKeys();
      }
      if (this.player?.body) {
        this.player.setVelocity(0, 0);
      }
      if (this.player) {
        this.player.isKnockedBack = false;
      }
    };

    this.game.events.on(Phaser.Core.Events.BLUR, () => resetInputState());
    this.game.events.on(Phaser.Core.Events.FOCUS, () => resetInputState());

    this.visibilityHandler = () => {
      if (document.hidden) resetInputState();
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    // Camera follows player
    this.cameras.main.startFollow(this.player, true, 0.1, 0.1);
    this.cameras.main.setBounds(0, 0, config.width, config.height);

    // Create HUD (level-specific)
    this.createHUD();

    // Start gameplay music
    this.music = this.sound.add('gameplay_music', {
      loop: true,
      volume: 0.5,
    });
    this.music.play();

    // Cheat code input listener
    this.input.keyboard.on('keydown', (event) => {
      this.handleCheatInput(event);
    });
  }

  update(time) {
    // Skip update if player doesn't exist
    if (!this.player) return;

    // Player movement
    this.handlePlayerMovement();

    // Zombie AI - chase player
    this.zombies.children.iterate((zombie) => {
      if (zombie && zombie.active && !zombie.markedForDeath) {
        this.physics.moveToObject(zombie, this.player, ZOMBIE_SPEED_SHAMBLER);

        // Face the player
        const angle = Phaser.Math.Angle.Between(zombie.x, zombie.y, this.player.x, this.player.y);
        zombie.setRotation(angle + Math.PI / 2);
      }
    });

    // Shooting (blocked while cheat input is active)
    if (!this.cheatInputActive && this.wasd.shoot.isDown && time > this.lastFired + 250) {
      this.shoot();
      this.lastFired = time;
    }

    // Update homing rockets (track targets)
    this.updateHomingRockets();

    // Update HUD (level-specific)
    this.updateHUD();
  }

  shutdown() {
    // Remove document event listener
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }

    // Cancel any active rocket lifetime timers
    if (this.homingRockets) {
      this.homingRockets.getChildren().forEach(rocket => {
        if (rocket.lifetime) {
          rocket.lifetime.remove();
          rocket.lifetime = null;
        }
      });
    }

    // Cancel rotor timer if active
    if (this.rotorTimer) {
      this.rotorTimer.destroy();
      this.rotorTimer = null;
    }

    // Stop music
    if (this.music) {
      this.music.stop();
      this.music = null;
    }
  }

  // ==================== PLAYER SYSTEM ====================

  createPlayer() {
    const config = this.getLevelConfig();

    // Use the tank sprite (306x442, points UP by default)
    this.player = this.physics.add.sprite(config.width / 4, config.height / 4, 'tank');

    // Scale down the sprite to ~50px wide
    this.player.setScale(0.15);

    // Configure physics body
    this.player.setCollideWorldBounds(true);

    // Disable friction to prevent wall-sticking
    this.player.setPushable(false);
    this.player.body.setFriction(0, 0);

    // Adjust physics body
    this.player.body.setSize(250, 350);
    this.player.body.setOffset(28, 46);

    this.player.health = 100;
    this.player.direction = 'up';
    this.player.rotation = 0;
    this.player.isShooting = false;
    this.player.isKnockedBack = false;
    this.player.isInvulnerable = false;
  }

  /**
   * Get current player speed (can be overridden for speed boosts)
   */
  getPlayerSpeed() {
    return PLAYER_SPEED;
  }

  handlePlayerMovement() {
    if (!this.player || this.player.isKnockedBack) return;

    // Block movement while cheat input is active
    if (this.cheatInputActive) {
      this.player.setVelocity(0, 0);
      return;
    }

    const speed = this.getPlayerSpeed();

    // Calculate input vectors
    const left = this.cursors.left.isDown || this.wasd.left.isDown;
    const right = this.cursors.right.isDown || this.wasd.right.isDown;
    const up = this.cursors.up.isDown || this.wasd.up.isDown;
    const down = this.cursors.down.isDown || this.wasd.down.isDown;

    const inputX = (right ? 1 : 0) - (left ? 1 : 0);
    const inputY = (down ? 1 : 0) - (up ? 1 : 0);

    // Calculate velocity
    let vx = inputX * speed;
    let vy = inputY * speed;

    // Normalize diagonal movement
    if (inputX !== 0 && inputY !== 0) {
      vx *= 0.7071;
      vy *= 0.7071;
    }

    // Apply velocity
    this.player.setVelocity(vx, vy);

    // Rotate tank to face movement direction
    if (inputX !== 0 || inputY !== 0) {
      const targetAngle = Math.atan2(inputY, inputX) + Math.PI / 2;
      this.player.setRotation(targetAngle);

      // Update direction for shooting
      if (Math.abs(inputX) > Math.abs(inputY)) {
        this.player.direction = inputX > 0 ? 'right' : 'left';
      } else {
        this.player.direction = inputY > 0 ? 'down' : 'up';
      }
    }
  }

  playerHitZombie(player, zombie) {
    if (player.isInvulnerable) return;
    if (zombie.markedForDeath) return;

    // Take damage
    player.health -= 10;

    // Set knockback state
    player.isKnockedBack = true;

    // Knockback
    const angle = Phaser.Math.Angle.Between(zombie.x, zombie.y, player.x, player.y);
    player.setVelocity(Math.cos(angle) * 300, Math.sin(angle) * 300);

    // Flash red
    player.setTint(0xff0000);

    // Set invulnerability
    player.isInvulnerable = true;

    // Visual effect for invulnerability
    this.tweens.add({
      targets: player,
      alpha: 0.5,
      duration: 100,
      yoyo: true,
      repeat: 4,
      onComplete: () => {
        if (player.active) player.alpha = 1;
      }
    });

    // Reset physics control
    this.time.delayedCall(200, () => {
      if (player.active) {
        player.isKnockedBack = false;
        player.clearTint();
      }
    });

    // Reset invulnerability
    this.time.delayedCall(1000, () => {
      if (player.active) {
        player.isInvulnerable = false;
      }
    });

    // Destroy zombie
    zombie.destroy();

    if (player.health <= 0) {
      this.gameOver();
    }
  }

  // ==================== WEAPON SYSTEM ====================

  shoot() {
    if (this.weaponMode === 'shell') {
      this.fireShell();
    } else {
      this.fireHomingRocket();
    }
  }

  fireShell() {
    // Calculate bullet spawn position at tank barrel
    const barrelOffset = 40;
    const angle = this.player.rotation - Math.PI / 2;
    const bulletX = this.player.x + Math.cos(angle) * barrelOffset;
    const bulletY = this.player.y + Math.sin(angle) * barrelOffset;

    const bullet = this.bullets.get(bulletX, bulletY);
    if (!bullet) return;

    // Play gunshot sound
    this.sound.play('gunshot', { volume: 0.3 });

    // Brief shooting state
    this.player.isShooting = true;
    this.time.delayedCall(100, () => {
      if (this.player.active) {
        this.player.isShooting = false;
      }
    });

    bullet.setActive(true);
    bullet.setVisible(true);
    bullet.body.enable = true;

    // Shoot in direction tank is facing
    const bulletSpeed = 400;
    const vx = Math.cos(angle) * bulletSpeed;
    const vy = Math.sin(angle) * bulletSpeed;

    bullet.setVelocity(vx, vy);

    // Destroy bullet after 1 second
    this.time.delayedCall(1000, () => {
      if (bullet.active) {
        bullet.setActive(false);
        bullet.setVisible(false);
        bullet.body.enable = false;
      }
    });
  }

  fireHomingRocket() {
    // Check ammo
    if (this.homingRocketsRemaining <= 0) {
      this.weaponMode = 'shell';
      this.updateWeaponModeDisplay();
      return;
    }

    // Find nearest zombie
    const target = this.findNearestZombie();
    if (!target) return;

    // Spawn rocket at tank position
    const rocket = this.homingRockets.get(this.player.x, this.player.y);
    if (!rocket) return;

    rocket.setActive(true);
    rocket.setVisible(true);
    rocket.body.enable = true;

    // Store target and speed on rocket
    rocket.target = target;
    rocket.speed = ROCKET_SPEED;

    // Set initial rotation toward target
    const angle = Phaser.Math.Angle.Between(
      this.player.x, this.player.y,
      target.x, target.y
    );
    rocket.setRotation(angle + Math.PI / 2);

    // Play launch sound
    this.sound.play('gunshot', { volume: 0.4, rate: 0.8 });

    // Add lifetime timeout
    rocket.lifetime = this.time.delayedCall(ROCKET_LIFETIME, () => {
      if (rocket.active) this.explodeRocket(rocket);
    });

    // Decrement ammo
    this.homingRocketsRemaining--;
    this.updateWeaponModeDisplay();
  }

  findNearestZombie(fromX = this.player?.x, fromY = this.player?.y) {
    if (fromX === undefined || fromY === undefined) return null;

    let nearest = null;
    let minDist = Infinity;

    this.zombies.getChildren().forEach(zombie => {
      if (!zombie.active || !zombie.body || zombie.markedForDeath) return;
      const dist = Phaser.Math.Distance.Between(fromX, fromY, zombie.x, zombie.y);
      if (dist < minDist) {
        minDist = dist;
        nearest = zombie;
      }
    });

    return nearest;
  }

  updateHomingRockets() {
    this.homingRockets.getChildren().forEach(rocket => {
      if (!rocket.active) return;

      // Check if target is still valid
      if (!rocket.target || !rocket.target.active || !rocket.target.body) {
        const newTarget = this.findNearestZombie(rocket.x, rocket.y);
        if (newTarget) {
          rocket.target = newTarget;
        } else {
          this.explodeRocket(rocket);
          return;
        }
      }

      // Move toward target
      this.physics.moveToObject(rocket, rocket.target, rocket.speed);

      // Rotate sprite to face direction of travel
      const angle = Phaser.Math.Angle.Between(
        rocket.x, rocket.y,
        rocket.target.x, rocket.target.y
      );
      rocket.setRotation(angle + Math.PI / 2);
    });
  }

  rocketHitZombie(rocket, zombie) {
    this.explodeRocket(rocket);
  }

  explodeRocket(rocket) {
    const x = rocket.x;
    const y = rocket.y;

    // Cancel lifetime timer
    if (rocket.lifetime) {
      rocket.lifetime.remove();
      rocket.lifetime = null;
    }

    // Kill zombies within blast radius
    this.zombies.getChildren().forEach(zombie => {
      if (!zombie.active || zombie.markedForDeath) return;
      const dist = Phaser.Math.Distance.Between(x, y, zombie.x, zombie.y);
      if (dist <= BLAST_RADIUS) {
        zombie.markedForDeath = true;
        if (zombie.body) zombie.body.enable = false;
        this.score += 100;

        const zombieX = zombie.x;
        const zombieY = zombie.y;

        zombie.play('zombie_death');

        this.time.delayedCall(500, () => {
          if (zombie.active) {
            if (Math.random() < ROCKET_PICKUP_CHANCE) {
              this.spawnRocketPickup(zombieX, zombieY);
            }
            zombie.destroy();
          }
        });
      }
    });

    // Play explosion animation
    this.playExplosion(x, y);

    // Play impact sound
    this.sound.play('impact', { volume: 0.5 });

    // Deactivate rocket
    rocket.setActive(false);
    rocket.setVisible(false);
    rocket.body.enable = false;
    rocket.target = null;
  }

  playExplosion(x, y) {
    const frames = ['explosion_1', 'explosion_2', 'explosion_3', 'explosion_4'];
    let frameIndex = 0;

    const explosion = this.add.image(x, y, frames[0]);
    explosion.setDepth(50);

    const advanceFrame = () => {
      frameIndex++;
      if (frameIndex < frames.length) {
        explosion.setTexture(frames[frameIndex]);
        explosion.setScale(1 + frameIndex * 0.3);
        this.time.delayedCall(100, advanceFrame);
      } else {
        explosion.destroy();
      }
    };

    this.time.delayedCall(100, advanceFrame);
  }

  switchWeaponMode() {
    if (this.weaponMode === 'shell') {
      if (this.homingRocketsRemaining > 0) {
        this.weaponMode = 'homing';
        this.updateWeaponModeDisplay();
      }
    } else {
      this.weaponMode = 'shell';
      this.updateWeaponModeDisplay();
    }
  }

  updateWeaponModeDisplay() {
    // Default implementation - subclass should override if needed
    // Move selector to current weapon
    if (this.weaponSelector) {
      if (this.weaponMode === 'shell') {
        this.weaponSelector.setPosition(32, GAME_HEIGHT - 32);
      } else {
        this.weaponSelector.setPosition(80, GAME_HEIGHT - 32);
      }
    }

    // Update rocket count
    if (this.rocketCountText) {
      this.rocketCountText.setText(`x${this.homingRocketsRemaining}`);
    }

    // Gray out rockets if none remaining
    if (this.rocketIcon) {
      if (this.homingRocketsRemaining <= 0) {
        this.rocketIcon.setTint(0x666666);
      } else {
        this.rocketIcon.clearTint();
      }
    }
  }

  spawnRocketPickup(x, y) {
    const pickup = this.rocketPickups.create(x, y, 'rocket_pickup');
    pickup.setCollideWorldBounds(true);
    pickup.setDepth(10);

    // Auto-despawn after 10 seconds
    this.time.delayedCall(10000, () => {
      if (pickup.active) {
        pickup.setAlpha(0.5);
        this.time.delayedCall(2000, () => {
          if (pickup.active) pickup.destroy();
        });
      }
    });
  }

  collectRocketPickup(player, pickup) {
    if (this.homingRocketsRemaining < HOMING_ROCKETS_MAX) {
      this.homingRocketsRemaining = Math.min(
        this.homingRocketsRemaining + 1,
        HOMING_ROCKETS_MAX
      );
      this.updateWeaponModeDisplay();
      pickup.destroy();
    }
  }

  bulletHitZombie(bullet, zombie) {
    bullet.setActive(false);
    bullet.setVisible(false);
    bullet.body.enable = false;

    if (zombie.markedForDeath) return;
    zombie.markedForDeath = true;

    // Play impact sound
    this.sound.play('impact', { volume: 0.4 });

    const zombieX = zombie.x;
    const zombieY = zombie.y;

    zombie.body.enable = false;
    zombie.play('zombie_death');

    this.time.delayedCall(500, () => {
      if (zombie.active) {
        if (Math.random() < ROCKET_PICKUP_CHANCE) {
          this.spawnRocketPickup(zombieX, zombieY);
        }
        zombie.destroy();
      }
    });

    this.score += 100;
  }

  // ==================== ZOMBIE SYSTEM ====================

  spawnZombies(count) {
    const config = this.getLevelConfig();

    for (let i = 0; i < count; i++) {
      const x = Phaser.Math.Between(100, config.width - 100);
      const y = Phaser.Math.Between(100, config.height - 100);

      // Don't spawn too close to player
      if (this.player) {
        const distToPlayer = Phaser.Math.Distance.Between(x, y, this.player.x, this.player.y);
        if (distToPlayer < config.zombieSpawnDistance) {
          i--;
          continue;
        }
      }

      this.createZombie(x, y);
    }
  }

  createZombie(x, y) {
    const zombie = this.zombies.create(x, y, 'zombie_shambler_walk');
    zombie.setScale(0.15);
    zombie.play('zombie_walk');
    zombie.setCollideWorldBounds(true);
    return zombie;
  }

  // ==================== AIRSTRIKE SYSTEM ====================

  callAirstrike() {
    // Check if airstrike is available
    if (!this.airstrikeAvailable || this.activeHelicopter) {
      return;
    }

    // Find the closest zombie to the player
    let closestZombie = null;
    let closestDist = Infinity;

    this.zombies.children.iterate((zombie) => {
      if (zombie && zombie.active && !zombie.markedForDeath) {
        const dist = Phaser.Math.Distance.Between(
          this.player.x, this.player.y,
          zombie.x, zombie.y
        );
        if (dist < closestDist) {
          closestDist = dist;
          closestZombie = zombie;
        }
      }
    });

    // No zombies to target
    if (!closestZombie) {
      return;
    }

    // Mark airstrike as in use
    this.airstrikeAvailable = false;

    // Determine entry point (furthest edge from player)
    const config = this.getLevelConfig();
    const enterFromRight = this.player.x < config.width / 2;

    const entryX = enterFromRight ? config.width + 150 : -150;
    const entryY = closestZombie.y;

    // Create helicopter sprite
    const helicopter = this.add.sprite(entryX, entryY, 'helicopter_0');
    helicopter.setScale(0.25);
    helicopter.setDepth(100);
    helicopter.rotorFrame = 0;

    this.activeHelicopter = helicopter;

    // Store target zombie reference and flight data
    helicopter.targetZombie = closestZombie;
    helicopter.bulletsShot = 0;
    helicopter.enterFromRight = enterFromRight;

    // Calculate initial rotation to face the target
    const initialAngle = Phaser.Math.Angle.Between(
      entryX, entryY,
      closestZombie.x, closestZombie.y
    );
    helicopter.setRotation(initialAngle + Math.PI);

    // Start rotor animation
    this.rotorTimer = this.time.addEvent({
      delay: 50,
      loop: true,
      callback: () => {
        if (helicopter.active) {
          helicopter.rotorFrame = helicopter.rotorFrame === 0 ? 1 : 0;
          helicopter.setTexture(`helicopter_${helicopter.rotorFrame}`);
        }
      }
    });

    // Approach from an offset position
    const approachX = closestZombie.x + (enterFromRight ? 100 : -100);
    const approachY = closestZombie.y;

    this.tweens.add({
      targets: helicopter,
      x: approachX,
      y: approachY,
      duration: 1500,
      ease: 'Sine.easeOut',
      onUpdate: () => {
        // Smoothly rotate helicopter to face movement direction
        if (helicopter.active && helicopter.targetZombie && helicopter.targetZombie.active) {
          const targetAngle = Phaser.Math.Angle.Between(
            helicopter.x, helicopter.y,
            helicopter.targetZombie.x, helicopter.targetZombie.y
          ) + Math.PI;
          helicopter.rotation = Phaser.Math.Angle.RotateTo(
            helicopter.rotation,
            targetAngle,
            0.1
          );
        }
      },
      onComplete: () => {
        this.fireHelicopterBullets(helicopter);
      }
    });
  }

  fireHelicopterBullets(helicopter) {
    if (!helicopter.active || !helicopter.targetZombie) {
      this.finishAirstrike(helicopter);
      return;
    }

    const targetZombie = helicopter.targetZombie;
    const bulletsToFire = 6;
    let bulletsFired = 0;

    if (targetZombie.heliHits === undefined) {
      targetZombie.heliHits = 0;
    }

    const fireTimer = this.time.addEvent({
      delay: 200,
      repeat: bulletsToFire - 1,
      callback: () => {
        if (!helicopter.active || !targetZombie.active) {
          fireTimer.destroy();
          this.finishAirstrike(helicopter);
          return;
        }

        // Alternate gun positions
        const gunSideOffset = bulletsFired % 2 === 0 ? -20 : 20;
        const gunForwardOffset = 30;

        const forwardAngle = helicopter.rotation - Math.PI;
        const sideAngle = helicopter.rotation - Math.PI / 2;

        const bulletX = helicopter.x +
          Math.cos(forwardAngle) * gunForwardOffset +
          Math.cos(sideAngle) * gunSideOffset;
        const bulletY = helicopter.y +
          Math.sin(forwardAngle) * gunForwardOffset +
          Math.sin(sideAngle) * gunSideOffset;

        // Create golden bullet
        const bullet = this.add.circle(bulletX, bulletY, 5, 0xFFD700);
        bullet.setDepth(99);
        this.physics.add.existing(bullet);
        bullet.body.setCircle(5);

        // Play gunshot sound
        this.sound.play('gunshot', { volume: 0.25, rate: 1.2 });

        // Calculate angle to zombie
        const angle = Phaser.Math.Angle.Between(
          bulletX, bulletY,
          targetZombie.x, targetZombie.y
        );

        // Set bullet velocity toward zombie
        const bulletSpeed = 600;
        bullet.body.setVelocity(
          Math.cos(angle) * bulletSpeed,
          Math.sin(angle) * bulletSpeed
        );

        bullet.targetZombie = targetZombie;

        // Check for collision
        this.physics.add.overlap(bullet, targetZombie, (b, z) => {
          this.helicopterBulletHitZombie(b, z);
        });

        // Destroy bullet after 2 seconds
        this.time.delayedCall(2000, () => {
          if (bullet.active) {
            bullet.destroy();
          }
        });

        bulletsFired++;

        if (bulletsFired >= bulletsToFire) {
          this.time.delayedCall(500, () => {
            this.finishAirstrike(helicopter);
          });
        }
      }
    });
  }

  helicopterBulletHitZombie(bullet, zombie) {
    bullet.destroy();

    if (zombie.markedForDeath) return;

    this.sound.play('impact', { volume: 0.25, rate: 1.1 });

    if (zombie.heliHits === undefined) {
      zombie.heliHits = 0;
    }

    zombie.heliHits++;

    // Pulse red effect
    zombie.setTint(0xff0000);
    this.time.delayedCall(100, () => {
      if (zombie.active && !zombie.markedForDeath) {
        zombie.clearTint();
      }
    });

    // Check if zombie should die (6 hits)
    if (zombie.heliHits >= 6) {
      zombie.markedForDeath = true;
      zombie.body.enable = false;

      zombie.play('zombie_death');

      this.time.delayedCall(500, () => {
        if (zombie.active) {
          zombie.destroy();
        }
      });

      this.score += 150;
    }
  }

  finishAirstrike(helicopter) {
    if (!helicopter.active) return;

    const config = this.getLevelConfig();
    const exitX = helicopter.enterFromRight ? -150 : config.width + 150;
    const exitY = helicopter.y + Phaser.Math.Between(-100, 100);

    const exitAngle = Phaser.Math.Angle.Between(
      helicopter.x, helicopter.y,
      exitX, exitY
    ) + Math.PI;

    this.tweens.add({
      targets: helicopter,
      x: exitX,
      y: exitY,
      duration: 2000,
      ease: 'Sine.easeIn',
      onUpdate: () => {
        if (helicopter.active) {
          helicopter.rotation = Phaser.Math.Angle.RotateTo(
            helicopter.rotation,
            exitAngle,
            0.05
          );
        }
      },
      onComplete: () => {
        if (this.rotorTimer) {
          this.rotorTimer.destroy();
          this.rotorTimer = null;
        }
        helicopter.destroy();
        this.activeHelicopter = null;

        this.time.delayedCall(5000, () => {
          this.airstrikeAvailable = true;
        });
      }
    });
  }

  // ==================== CHEAT CODE SYSTEM ====================

  handleCheatInput(event) {
    const key = event.key;

    // "/" opens cheat input
    if (key === '/' && !this.cheatInputActive) {
      event.preventDefault();
      this.openCheatInput();
      return;
    }

    // Handle input when cheat mode is active
    if (this.cheatInputActive) {
      event.preventDefault();
      event.stopPropagation();

      if (key === 'Escape') {
        this.closeCheatInput();
      } else if (key === 'Enter') {
        this.submitCheatCode();
      } else if (key === 'Backspace') {
        this.cheatBuffer = this.cheatBuffer.slice(0, -1);
        this.updateCheatInputDisplay();
      } else if (key.length === 1 && /^[a-zA-Z0-9!]$/.test(key)) {
        this.cheatBuffer += key.toLowerCase();
        this.updateCheatInputDisplay();
      }
    }
  }

  openCheatInput() {
    this.cheatInputActive = true;
    this.cheatBuffer = '';

    // Create input display
    this.cheatInputBg = this.add.rectangle(GAME_WIDTH / 2, 50, 300, 40, 0x000000, 0.8);
    this.cheatInputBg.setScrollFactor(0);
    this.cheatInputBg.setDepth(1000);
    this.cheatInputBg.setStrokeStyle(2, 0x00ff00);

    this.cheatInputText = this.add.text(GAME_WIDTH / 2, 50, '/', {
      fontFamily: 'Courier New',
      fontSize: '20px',
      fill: '#00ff00',
    });
    this.cheatInputText.setOrigin(0.5);
    this.cheatInputText.setScrollFactor(0);
    this.cheatInputText.setDepth(1001);

    this.cheatHintText = this.add.text(GAME_WIDTH / 2, 75, 'Enter cheat code, press ENTER to submit', {
      fontFamily: 'Courier New',
      fontSize: '10px',
      fill: '#888888',
    });
    this.cheatHintText.setOrigin(0.5);
    this.cheatHintText.setScrollFactor(0);
    this.cheatHintText.setDepth(1001);
  }

  updateCheatInputDisplay() {
    if (this.cheatInputText) {
      this.cheatInputText.setText('/' + this.cheatBuffer + '_');
    }
  }

  closeCheatInput() {
    this.cheatInputActive = false;
    this.cheatBuffer = '';

    if (this.cheatInputBg) {
      this.cheatInputBg.destroy();
      this.cheatInputBg = null;
    }
    if (this.cheatInputText) {
      this.cheatInputText.destroy();
      this.cheatInputText = null;
    }
    if (this.cheatHintText) {
      this.cheatHintText.destroy();
      this.cheatHintText = null;
    }
  }

  submitCheatCode() {
    const code = this.cheatBuffer.toLowerCase();
    this.closeCheatInput();

    if (code === 'iamthor') {
      this.activateThorCheat();
    } else if (code === 'next!') {
      this.activateNextLevelCheat();
    } else if (code === 'endgame') {
      this.activateEndGameCheat();
    } else {
      this.showCheatMessage('INVALID CODE');
    }
  }

  activateEndGameCheat() {
    this.showCheatMessage('ENDING GAME');

    if (this.music) {
      this.tweens.add({
        targets: this.music,
        volume: 0,
        duration: 500,
      });
    }

    this.time.delayedCall(1000, () => {
      this.cameras.main.fadeOut(500, 0, 0, 0);
    });

    this.cameras.main.once('camerafadeoutcomplete', () => {
      if (this.music) this.music.stop();
      this.scene.start('AnimatedCreditsScene');
    });
  }

  activateThorCheat() {
    if (this.thorHelicopterActive) return;

    const zombieCount = this.zombies.getChildren().filter(z => z.active && !z.markedForDeath).length;
    if (zombieCount === 0) {
      this.showCheatMessage('NO TARGETS');
      return;
    }

    this.thorHelicopterActive = true;
    this.showCheatMessage('THOR MODE ACTIVATED');

    // Start the Thor airstrike loop
    this.thorCallNextAirstrike();
  }

  thorCallNextAirstrike() {
    // Find the closest zombie to the player
    let closestZombie = null;
    let closestDist = Infinity;

    this.zombies.children.iterate((zombie) => {
      if (zombie && zombie.active && !zombie.markedForDeath) {
        const dist = Phaser.Math.Distance.Between(
          this.player.x, this.player.y,
          zombie.x, zombie.y
        );
        if (dist < closestDist) {
          closestDist = dist;
          closestZombie = zombie;
        }
      }
    });

    // No more zombies - Thor mode complete
    if (!closestZombie) {
      this.thorHelicopterActive = false;
      this.showCheatMessage('ALL TARGETS ELIMINATED');
      return;
    }

    // Determine entry point (furthest edge from player)
    const config = this.getLevelConfig();
    const enterFromRight = this.player.x < config.width / 2;

    const entryX = enterFromRight ? config.width + 150 : -150;
    const entryY = closestZombie.y;

    // Create helicopter sprite with golden tint
    const helicopter = this.add.sprite(entryX, entryY, 'helicopter_0');
    helicopter.setScale(0.25);
    helicopter.setDepth(100);
    helicopter.rotorFrame = 0;
    helicopter.setTint(0xFFD700);

    this.activeHelicopter = helicopter;

    // Store target zombie reference and flight data
    helicopter.targetZombie = closestZombie;
    helicopter.bulletsShot = 0;
    helicopter.enterFromRight = enterFromRight;
    helicopter.isThor = true;

    // Calculate initial rotation to face the target
    const initialAngle = Phaser.Math.Angle.Between(
      entryX, entryY,
      closestZombie.x, closestZombie.y
    );
    helicopter.setRotation(initialAngle + Math.PI);

    // Start rotor animation
    this.rotorTimer = this.time.addEvent({
      delay: 50,
      loop: true,
      callback: () => {
        if (helicopter.active) {
          helicopter.rotorFrame = helicopter.rotorFrame === 0 ? 1 : 0;
          helicopter.setTexture(`helicopter_${helicopter.rotorFrame}`);
          helicopter.setTint(0xFFD700);
        }
      }
    });

    // Approach from an offset position
    const approachX = closestZombie.x + (enterFromRight ? 100 : -100);
    const approachY = closestZombie.y;

    this.tweens.add({
      targets: helicopter,
      x: approachX,
      y: approachY,
      duration: 1500,
      ease: 'Sine.easeOut',
      onUpdate: () => {
        if (helicopter.active && helicopter.targetZombie && helicopter.targetZombie.active) {
          const targetAngle = Phaser.Math.Angle.Between(
            helicopter.x, helicopter.y,
            helicopter.targetZombie.x, helicopter.targetZombie.y
          ) + Math.PI;
          helicopter.rotation = Phaser.Math.Angle.RotateTo(
            helicopter.rotation,
            targetAngle,
            0.1
          );
        }
      },
      onComplete: () => {
        this.thorFireHelicopterBullets(helicopter);
      }
    });
  }

  thorFireHelicopterBullets(helicopter) {
    if (!helicopter.active || !helicopter.targetZombie) {
      this.thorFinishAirstrike(helicopter);
      return;
    }

    const targetZombie = helicopter.targetZombie;
    const bulletsToFire = 6;
    let bulletsFired = 0;

    if (targetZombie.heliHits === undefined) {
      targetZombie.heliHits = 0;
    }

    const fireTimer = this.time.addEvent({
      delay: 200,
      repeat: bulletsToFire - 1,
      callback: () => {
        if (!helicopter.active || !targetZombie.active) {
          fireTimer.destroy();
          this.thorFinishAirstrike(helicopter);
          return;
        }

        // Alternate gun positions
        const gunSideOffset = bulletsFired % 2 === 0 ? -20 : 20;
        const gunForwardOffset = 30;

        const forwardAngle = helicopter.rotation - Math.PI;
        const sideAngle = helicopter.rotation - Math.PI / 2;

        const bulletX = helicopter.x +
          Math.cos(forwardAngle) * gunForwardOffset +
          Math.cos(sideAngle) * gunSideOffset;
        const bulletY = helicopter.y +
          Math.sin(forwardAngle) * gunForwardOffset +
          Math.sin(sideAngle) * gunSideOffset;

        // Create golden bullet
        const bullet = this.add.circle(bulletX, bulletY, 5, 0xFFD700);
        bullet.setDepth(99);
        this.physics.add.existing(bullet);
        bullet.body.setCircle(5);

        // Play gunshot sound
        this.sound.play('gunshot', { volume: 0.25, rate: 1.2 });

        // Calculate angle to zombie
        const angle = Phaser.Math.Angle.Between(
          bulletX, bulletY,
          targetZombie.x, targetZombie.y
        );

        // Set bullet velocity toward zombie
        const bulletSpeed = 600;
        bullet.body.setVelocity(
          Math.cos(angle) * bulletSpeed,
          Math.sin(angle) * bulletSpeed
        );

        bullet.targetZombie = targetZombie;

        // Check for collision - use existing hit handler
        this.physics.add.overlap(bullet, targetZombie, (b, z) => {
          this.helicopterBulletHitZombie(b, z);
        });

        // Destroy bullet after 2 seconds
        this.time.delayedCall(2000, () => {
          if (bullet.active) {
            bullet.destroy();
          }
        });

        bulletsFired++;

        if (bulletsFired >= bulletsToFire) {
          this.time.delayedCall(500, () => {
            this.thorFinishAirstrike(helicopter);
          });
        }
      }
    });
  }

  thorFinishAirstrike(helicopter) {
    if (!helicopter.active) {
      // Helicopter already destroyed, check for more zombies
      this.time.delayedCall(500, () => {
        this.thorCallNextAirstrike();
      });
      return;
    }

    // Check if there are more zombies to kill
    const remainingZombies = this.zombies.getChildren().filter(z => z.active && !z.markedForDeath).length;

    if (remainingZombies > 0) {
      // More zombies - find next target immediately without flying off
      const nextZombie = this.findNearestZombie(helicopter.x, helicopter.y);

      if (nextZombie) {
        helicopter.targetZombie = nextZombie;

        // Fly to next target
        const approachX = nextZombie.x + (helicopter.enterFromRight ? 100 : -100);
        const approachY = nextZombie.y;

        this.tweens.add({
          targets: helicopter,
          x: approachX,
          y: approachY,
          duration: 1000,
          ease: 'Sine.easeInOut',
          onUpdate: () => {
            if (helicopter.active && nextZombie && nextZombie.active) {
              const targetAngle = Phaser.Math.Angle.Between(
                helicopter.x, helicopter.y,
                nextZombie.x, nextZombie.y
              ) + Math.PI;
              helicopter.rotation = Phaser.Math.Angle.RotateTo(
                helicopter.rotation,
                targetAngle,
                0.1
              );
            }
          },
          onComplete: () => {
            this.thorFireHelicopterBullets(helicopter);
          }
        });
        return;
      }
    }

    // No more zombies - fly off screen
    const config = this.getLevelConfig();
    const exitX = helicopter.enterFromRight ? -150 : config.width + 150;
    const exitY = helicopter.y + Phaser.Math.Between(-100, 100);

    const exitAngle = Phaser.Math.Angle.Between(
      helicopter.x, helicopter.y,
      exitX, exitY
    ) + Math.PI;

    this.tweens.add({
      targets: helicopter,
      x: exitX,
      y: exitY,
      duration: 2000,
      ease: 'Sine.easeIn',
      onUpdate: () => {
        if (helicopter.active) {
          helicopter.rotation = Phaser.Math.Angle.RotateTo(
            helicopter.rotation,
            exitAngle,
            0.05
          );
        }
      },
      onComplete: () => {
        if (this.rotorTimer) {
          this.rotorTimer.destroy();
          this.rotorTimer = null;
        }
        helicopter.destroy();
        this.activeHelicopter = null;
        this.thorHelicopterActive = false;
        this.showCheatMessage('ALL TARGETS ELIMINATED');
      }
    });
  }

  showCheatMessage(message) {
    const cheatText = this.add.text(GAME_WIDTH / 2, 80, message, {
      fontFamily: 'Courier New',
      fontSize: '24px',
      fill: '#ff00ff',
      stroke: '#000000',
      strokeThickness: 4,
    });
    cheatText.setOrigin(0.5);
    cheatText.setScrollFactor(0);
    cheatText.setDepth(1000);

    this.tweens.add({
      targets: cheatText,
      alpha: { from: 1, to: 0.3 },
      duration: 200,
      yoyo: true,
      repeat: 3,
      onComplete: () => {
        this.tweens.add({
          targets: cheatText,
          alpha: 0,
          y: 60,
          duration: 500,
          delay: 1000,
          onComplete: () => cheatText.destroy()
        });
      }
    });
  }

  activateNextLevelCheat() {
    this.showCheatMessage('LEVEL SKIP!');

    if (this.music) {
      this.tweens.add({
        targets: this.music,
        volume: 0,
        duration: 500,
      });
    }

    this.time.delayedCall(1000, () => {
      this.cameras.main.fadeOut(500, 0, 0, 0);
    });

    this.cameras.main.once('camerafadeoutcomplete', () => {
      if (this.music) this.music.stop();
      this.scene.start(this.getNextLevelScene());
    });
  }

  // ==================== UTILITIES ====================

  showNotification(message) {
    const text = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, message, {
      fontFamily: 'Courier New',
      fontSize: '32px',
      fill: '#00ff00',
      stroke: '#000000',
      strokeThickness: 4,
    });
    text.setOrigin(0.5);
    text.setScrollFactor(0);
    text.setDepth(200);

    this.tweens.add({
      targets: text,
      alpha: 0,
      y: GAME_HEIGHT / 2 - 50,
      duration: 1500,
      ease: 'Power2',
      onComplete: () => text.destroy(),
    });
  }
}
